// 自由流停车解决方案 — 页面全部文案与配置集中管理，视图只负责渲染

export const hero = {
  eyebrow: '产品与解决方案 · 自由流停车',
  title: '不停车、不减速、不排队——自由流停车，让城市通行一路畅行',
  desc: '华辉数智以融合识别、无感结算与城市一张网能力，连接停车场、道路、车辆与车主，构建“可感知、可结算、可运营”的自由流停车一体化方案。',
  chips: ['融合标签识别', '无感支付', '全场景一张网', '云边端协同', '断网不停服'],
  primaryLabel: '咨询解决方案',
  primaryHref: '/contact/business',
  secondaryLabel: '探索全场景',
  secondaryHref: '#scenes',
  image:
    'https://readdy.ai/api/search-image?query=Realistic%20photograph%20of%20a%20modern%20outdoor%20smart%20parking%20lot%20on%20a%20bright%20day%2C%20rows%20of%20parked%20cars%20in%20clean%20painted%20bays%2C%20a%20car%20driving%20through%20the%20entrance%20with%20no%20boom%20barrier%2C%20clear%20asphalt%20surface%20with%20subtle%20in-ground%20sensor%20strips%2C%20city%20buildings%20and%20green%20trees%20in%20the%20background%2C%20natural%20daylight%2C%20wide%20angle%20architectural%20photography%2C%20clean%20composition%2C%20high%20detail&width=1920&height=1080&seq=ff-hero-real-01&orientation=landscape&nocache=true',
  sceneLabels: [
    { icon: 'ri-roadster-line', label: '高速公路' },
    { icon: 'ri-road-map-line', label: '城市道路' },
    { icon: 'ri-parking-box-line', label: '停车场' },
  ],
};

export const concept = {
  eyebrow: '什么是自由流停车',
  title: '自由流停车，让通行不再为缴费停留',
  definition:
    '自由流停车，是车辆无需停车即可完成缴费的停车方式：在停车出行全场景部署智能识别设备，对自由行驶的车辆完成身份自动识别与费用无感结算。',
  cards: [
    {
      icon: 'ri-speed-up-line',
      title: '不停车',
      desc: '取消道闸与栏杆，车辆自由进出，出入口不再是通行瓶颈。',
    },
    {
      icon: 'ri-wallet-3-line',
      title: '不减速',
      desc: '融合标签与读卡器在行驶中完成识别与计费，车速不受影响。',
    },
    {
      icon: 'ri-timer-flash-line',
      title: '不排队',
      desc: '无感支付自动扣费，出入口零等待，高峰时段也能顺畅通行。',
    },
  ],
  tags: ['无感出入', '无感支付', '无人值守'],
};

export const painPoints = {
  eyebrow: '行业痛点',
  title: '停车出行，卡在六道关口',
  subtitle: '从找位到通行、从收费到治理，传统停车模式仍存在多重断点。',
  items: [
    { icon: 'ri-search-eye-line', title: '找位难', desc: '车主无法快速判断空余车位，绕行加剧拥堵。' },
    { icon: 'ri-git-merge-line', title: '通行堵', desc: '出入口道闸排队、高峰拥堵，收费站与城市节点尤甚。' },
    { icon: 'ri-wallet-3-line', title: '收费难', desc: '人工收费效率低，减免与对账流程繁琐。' },
    { icon: 'ri-alarm-warning-line', title: '逃费多', desc: '偷漏逃费频发，证据链弱、追缴无门。' },
    { icon: 'ri-coins-line', title: '成本高', desc: '人工值守与设备重复建设，运营成本居高不下。' },
    { icon: 'ri-bubble-chart-line', title: '孤岛化', desc: '车场、道路、商业停车资源相互独立，城市层面无法统筹。' },
  ],
};

export const overview = {
  eyebrow: '方案总览',
  title: '一套方案，贯通通行、计费、治理全链路',
  summary:
    '华辉数智自由流停车方案，以“车端标签、路侧感知、云边平台”协同为骨架，覆盖“通行、计费、缴费、稽核、追缴、信用”全链路，实现四大目标。',
  chips: ['融合识别', '无感结算', '稽核追缴', '信用治理'],
  goals: [
    {
      icon: 'ri-speed-up-line',
      title: '提高收费效率',
      desc: '自动识别计费，车辆快速通过不等待。',
    },
    {
      icon: 'ri-scales-3-line',
      title: '保障公平公正',
      desc: '流水凭证与稽核证据链，杜绝逃费漏费。',
    },
    {
      icon: 'ri-emotion-happy-line',
      title: '提升用户体验',
      desc: '无感通行、无感支付，出行更从容。',
    },
    {
      icon: 'ri-funds-box-line',
      title: '降低收费成本',
      desc: '无人值守、自动对账，减员增效轻资产运营。',
    },
  ],
};

export const compare = {
  eyebrow: '新旧对比',
  title: '传统停车模式 VS 自由流模式',
  subtitle: '同样是把车停好，两套模式在通行、识别、治理与数据上截然不同。',
  leftLabel: '传统停车模式',
  rightLabel: '自由流模式',
  rows: [
    { dim: '通行方式', left: '道闸 + 车牌识别，出入口排队等待', right: '无闸无杆，不停车、不减速、不排队通过' },
    { dim: '车辆识别', left: '单一车牌识别相机，多车并发易漏识', right: '融合标签 + 读卡器 + 高清视频多模互补识别' },
    { dim: '缴费方式', left: '人工收费、扫码缴费、ETC 混用', right: '全程无感自动扣费，一次行程、一次扣费、一次告知' },
    { dim: '场景覆盖', left: '停车场信息孤岛，各自为政', right: '高速 / 城市道路 / 路内泊位 / 停车场 / 充电桩一张网' },
    { dim: '运营成本', left: '人工巡查、收费、对账成本高', right: '无人值守、自动对账，减员增效' },
    { dim: '逃费治理', left: '证据链弱，追缴难', right: '稽核体系 + 追缴体系 + 征信黑名单闭环' },
    { dim: '数据价值', left: '数据沉睡，难以反哺运营', right: '城市交通实时动态图，支撑潮汐调控与政府决策' },
  ],
};

export const architecture = {
  eyebrow: '总体架构',
  title: '端—边—云三层架构，四大体系支撑全场景运行',
  subtitle: '从车端与路侧感知，到云边平台与业务应用，一套架构贯通自由流停车全链路。',
  layers: [],
  connect: {
    title: '把人、车、路、场连接起来',
    desc: '通过华辉数智的路侧与车端物联终端，将人、车、路、场连接成一张城市交通物联网。',
    nodes: [
      { icon: 'ri-user-3-line', label: '人', desc: '车主与运营人员' },
      { icon: 'ri-roadster-line', label: '车', desc: '自由流通行车辆' },
      { icon: 'ri-road-map-line', label: '路', desc: '高速与城市道路' },
      { icon: 'ri-parking-box-line', label: '场', desc: '车场与泊位设施' },
    ],
  },
};

export const productMatrix = {
  eyebrow: '核心产品矩阵',
  title: '从车端到云端，六类产品构成自由流底座',
  subtitle: '车端身份、路侧感知、视频取证与云端平台协同，支撑全场景自由流运行。',
  items: [],
  note: '“生态规划中”的产品为规划支持能力，具体形态以实际方案为准。',
};

export const scenes = {
  eyebrow: '全场景一张网',
  title: '一张网覆盖六大场景',
  subtitle: '从高速到路内泊位，自由流停车把分散的停车资源连成城市一张网。',
  items: [
    {
      id: 'sc-highway',
      icon: 'ri-roadster-line',
      title: '高速公路',
      image:
        'https://readdy.ai/api/search-image?query=Realistic%20photograph%20of%20a%20modern%20multi-lane%20highway%20at%20dusk%20with%20an%20overhead%20electronic%20toll%20gantry%20spanning%20the%20road%2C%20cars%20passing%20smoothly%20with%20glowing%20headlight%20and%20taillight%20trails%2C%20city%20skyline%20silhouette%20in%20the%20distance%2C%20blue%20hour%20lighting%2C%20wide%20angle%20long%20exposure%2C%20high%20detail%2C%20professional%20transportation%20photography&width=800&height=1000&seq=ff-scene-highway-02&orientation=portrait&nocache=true',
      alt: '高速公路自由流收费场景',
      deploy: '异常漏收点与收费站前布设门架式读卡器',
      work: '车辆不减速通过即完成识别计费，出入口不设杆、不收卡',
      value: '通行效率显著提升，收费过程无需人工干预',
    },
    {
      id: 'sc-city-road',
      icon: 'ri-road-map-line',
      title: '城市道路',
      image:
        'https://readdy.ai/api/search-image?query=Realistic%20photograph%20of%20a%20busy%20city%20arterial%20road%20at%20a%20signalized%20intersection%2C%20multiple%20lanes%20of%20cars%20and%20buses%2C%20modern%20glass%20office%20buildings%20and%20street%20trees%20on%20both%20sides%2C%20daytime%20clear%20sky%2C%20wide%20angle%20street%20photography%2C%20crisp%20detail%2C%20professional%20urban%20photography&width=800&height=1000&seq=ff-scene-cityroad-02&orientation=portrait&nocache=true',
      alt: '城市道路自由流感知场景',
      deploy: '红绿灯路口与拥堵路段布设地埋式感应带与门架设备',
      work: '按路段、时段灵活计费，用经济杠杆调节车流潮汐',
      value: '城市道路资源调配更精细，拥堵节点更可控',
    },
    {
      id: 'sc-onstreet',
      icon: 'ri-parking-box-line',
      title: '路内泊位',
      image:
        'https://readdy.ai/api/search-image?query=Realistic%20photograph%20of%20on-street%20parallel%20parking%20bays%20along%20a%20city%20curb%2C%20several%20cars%20parked%20neatly%20in%20painted%20spaces%20next%20to%20a%20sidewalk%20with%20trees%20and%20low%20buildings%2C%20small%20sensor%20pods%20on%20a%20light%20pole%2C%20soft%20daylight%2C%20moderate%20wide%20shot%2C%20high%20detail%2C%20professional%20street%20photography&width=800&height=1000&seq=ff-scene-onstreet-02&orientation=portrait&nocache=true',
      alt: '路内泊位自由流识别场景',
      deploy: '一字型泊位布设地埋式读卡器',
      work: '车辆驶入即计时、驶离即结单，利用现有监控杆位部署',
      value: '减员增效，路内泊位周转更高效',
    },
    {
      id: 'sc-lot',
      icon: 'ri-building-4-line',
      title: '停车场',
      image:
        'https://readdy.ai/api/search-image?query=Realistic%20photograph%20of%20a%20modern%20open-air%20outdoor%20parking%20lot%20with%20rows%20of%20parked%20cars%20in%20clean%20painted%20bays%2C%20an%20empty%20entrance%20with%20no%20boom%20barrier%2C%20bright%20blue%20sky%20with%20a%20few%20clouds%2C%20city%20buildings%20behind%2C%20wide%20angle%20architectural%20photography%2C%20high%20detail%2C%20professional%20commercial%20photography&width=800&height=1000&seq=ff-scene-lot-02&orientation=portrait&nocache=true',
      alt: '停车场无闸自由流进出场景',
      deploy: '出入口布设地埋式感应带，取消道闸自动扣费',
      work: '同步完成车场老式设备升级改造',
      value: '车场进出零等待，老旧车场低成本升级',
    },
    {
      id: 'sc-charging',
      icon: 'ri-charging-pile-line',
      title: '充电桩 / 洗车养护',
      image:
        'https://readdy.ai/api/search-image?query=Realistic%20photograph%20of%20a%20row%20of%20white%20electric%20vehicle%20charging%20piles%20in%20an%20outdoor%20parking%20lot%2C%20modern%20electric%20cars%20plugged%20in%20and%20charging%2C%20clean%20concrete%20pavement%20with%20painted%20parking%20lines%2C%20daylight%2C%20wide%20angle%2C%20high%20detail%2C%20professional%20energy%20technology%20photography&width=800&height=1000&seq=ff-scene-charging-02&orientation=portrait&nocache=true',
      alt: '充电桩洗车养护无感支付场景',
      deploy: '地埋式读卡器识别车辆',
      work: '按电量与服务费自动结算，停充洗一体化无感支付',
      value: '一次进场完成多项服务，消费体验更顺滑',
    },
    {
      id: 'sc-space',
      icon: 'ri-layout-masonry-line',
      title: '零碎空间',
      image:
        'https://readdy.ai/api/search-image?query=Realistic%20photograph%20of%20a%20small%20unused%20urban%20corner%20lot%20transformed%20into%20compact%20parking%20bays%20with%20a%20few%20parked%20cars%2C%20surrounded%20by%20city%20buildings%20and%20a%20low%20fence%2C%20subtle%20smart%20sensor%20posts%2C%20daytime%2C%20elevated%20wide%20angle%2C%20high%20detail%2C%20professional%20urban%20photography&width=800&height=1000&seq=ff-scene-space-02&orientation=portrait&nocache=true',
      alt: '零碎空间接入停车一张网场景',
      deploy: '闲置边角空地以最低改造成本接入一张网',
      work: '轻量化部署，快速形成可用泊位',
      value: '盘活城市停车资源，扩大供给',
    },
  ],
  adaptNote: '园区、商业综合体、医院、景区、社区等业态，均可按上述场景组合接入。',
};

export const flow = {
  eyebrow: '业务流程闭环',
  title: '从车辆识别，到信用管理的六步闭环',
  subtitle: '每一步都留下可追溯的流水与凭证，让自由流运行有据可依。',
  steps: [
    { icon: 'ri-scan-2-line', title: '车辆识别', desc: '融合标签 + 视频 / 牌识多模互补，生成通行流水并去重。' },
    { icon: 'ri-route-line', title: '路径识别', desc: '多点位流水拟合行驶路径，还原真实行程。' },
    { icon: 'ri-wallet-3-line', title: '计费缴费', desc: '按规则分段计费、自动扣费，消息实时推送，一次行程、一次扣费、一次告知。' },
    { icon: 'ri-shield-check-line', title: '稽核核验', desc: '异常通行与交易自动稽核，形成完整证据链。' },
    { icon: 'ri-refund-2-line', title: '欠费追缴', desc: '凭通行凭证线上追缴，多渠道提醒补缴。' },
    { icon: 'ri-award-line', title: '信用管理', desc: '建立车辆信用档案与等级评价，失信行为纳入黑名单联合治理。' },
  ],
  systemNote: '配套公开的收费规则与信用规则等制度保障，让自由流运行有据可依、有序可循。',
};

export const dataValue = {
  eyebrow: '城市数据价值',
  title: '城市停车驾驶舱，一屏看清城市停车',
  subtitle: '用一屏数据直观呈现城市停车运行状态，并向政府治理输出决策支撑。',
  stats: [
    { icon: 'ri-roadster-line', label: '今日停车', value: 128650, sub: '车辆进场' },
    { icon: 'ri-car-line', label: '当前停车', value: 8920, sub: '场内车辆' },
    { icon: 'ri-parking-box-line', label: '空余车位', value: 2386, sub: '实时空位' },
    { icon: 'ri-building-4-line', label: '在线停车场', value: 126, sub: '在线车场' },
  ],
  panelsTitle: '数据分析模块',
  panels: [
    { icon: 'ri-map-2-line', label: '城市停车热力图' },
    { icon: 'ri-flow-chart', label: '实时车流' },
    { icon: 'ri-line-chart-line', label: '高峰趋势' },
    { icon: 'ri-timer-line', label: '停车时长' },
    { icon: 'ri-eye-line', label: '异常车辆' },
    { icon: 'ri-shield-check-line', label: '稽核' },
    { icon: 'ri-refund-2-line', label: '追缴' },
    { icon: 'ri-award-line', label: '信用' },
  ],
  note: '以上为界面示意数据，用于展示城市停车数据中心能力，非真实业务数据。',
  values: [
    { icon: 'ri-alarm-warning-line', title: '政府应急指挥', desc: '接入城市指挥中心，突发事件协同调度。' },
    { icon: 'ri-government-line', title: '静态交通资源管控', desc: '全市停车资源统一管理调配。' },
    { icon: 'ri-line-chart-line', title: '大数据决策分析', desc: '与动态交通、治安、智慧城市数据交互。' },
    { icon: 'ri-service-line', title: '公众服务输出', desc: '出行服务、拥堵预警、停车导流。' },
  ],
  highlight:
    '基于交通实时动态图，对特定路段与时段实行差异化收费，经诱导屏、车主终端公告，以经济杠杆调节车流潮汐。',
};

export const faq = {
  eyebrow: '常见问题',
  title: '关于自由流停车，你可能会问',
  subtitle: '五个高频问题，帮你在咨询前先建立基本认知。',
  items: [
    {
      q: '自由流停车和传统 ETC 停车有什么区别？',
      a: '自由流停车以多模互补识别替代单一识别手段，实现无闸全场景通行，并通过稽核与信用体系形成闭环治理，覆盖高速、城市道路、路内泊位与停车场等更多场景。',
    },
    {
      q: '没有 ETC 设备的车辆怎么用？',
      a: '方案兼容非 ETC 车辆，通过高清视频与车牌识别补全车辆身份，并接入多种支付方式完成无感结算，不要求所有车辆都装有 ETC 设备。',
    },
    {
      q: '老停车场改造成本高吗？',
      a: '方案依托现有杆件与龙门架进行低成本改造，地埋设备隐蔽部署、施工量小，可同步完成老式设备升级，投入相对可控。',
    },
    {
      q: '逃费问题如何解决？',
      a: '以稽核证据链、线上追缴、征信黑名单三层治理，形成从识别、取证到追缴的完整闭环，降低偷漏逃费带来的损失。',
    },
    {
      q: '断网了车场还能正常运行吗？',
      a: '平台采用云边协同架构，边缘计算负责兜底，断网不停服，核心的通行与计费能力在离线状态下仍可继续运行。',
    },
  ],
};

export const cta = {
  title: '让城市通行一路畅行，从一次咨询开始',
  desc: '欢迎政府平台、停车运营方与生态伙伴洽谈合作。',
  primaryLabel: '咨询解决方案',
  secondaryLabel: '预约产品演示',
  href: '/contact/business',
};

export const related = {
  eyebrow: '相关推荐',
  title: '继续了解相关方案与产品',
  items: [
    { icon: 'ri-restaurant-line', name: '智慧餐饮', desc: '门店数字化与厨房协同', href: '/solutions/smart-catering' },
    { icon: 'ri-plant-line', name: '智慧农业', desc: '精准种植与农田数字化', href: '/solutions/smart-agriculture' },
    { icon: 'ri-red-packet-line', name: '数字红包', desc: '数字人民币红包与消费促进', href: '/solutions/red-packet' },
    { icon: 'ri-camera-3-line', name: '智能视觉监控', desc: '高清视频监控与智能分析', href: '/products' },
    { icon: 'ri-cpu-line', name: '智慧物联', desc: '环境与设备感知采集', href: '/products' },
  ],
};